import { useLazyQuery } from '@apollo/react-hooks';
import { getVariables } from '@atlassian/jira-business-common';
import { useProject } from '@atlassian/jira-business-entity-project-hook';
import { useFilteredViewContextInput } from '@atlassian/jira-business-filters';
import {
	getAGGCompliantData,
	useViewUpdater,
	createViewUpdaterCallbacksWithCacheActions,
	useCacheActions,
	getParentIssueId,
	type UpdaterReturn,
	isGroupItem,
} from '@atlassian/jira-business-list-handlers';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { ff } from '@atlassian/jira-feature-flagging';
import { STATUS_TYPE } from '@atlassian/jira-platform-field-config';
import { toIssueId } from '@atlassian/jira-shared-types';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';
import { getGroupQuery } from '../../services/get-item-groups/utils';
import type {
	JWMListViewItemDetails,
	JWMListViewItemDetails_jira_list_JiraWorkManagementViewItemConnection as JWMListViewItemDetailsEdges,
	JWMListViewItemDetails_jira_list_JiraWorkManagementViewItemConnection_edges_node as JWMListViewItemDetailsNode,
	JWMListViewItemDetailsVariables,
} from '../../services/get-list-view-issues/__generated_apollo__/JWMListViewItemDetails';
import {
	LIST_VIEW_QUERY,
	LIST_VIEW_ITEM_DETAILS_QUERY,
} from '../../services/get-list-view-issues/gql';
import type { ListViewIssue } from '../../services/get-list-view-issues/types';
import {
	addChildToParentListView,
	removeChildFromParentListView,
} from '../../services/get-list-view-issues/utils';
import { useHierarchyLevel } from '../../services/hierarchy-level';
import { useBulkSelectionStore } from '../bulk-selection';
import { useGroupBy } from '../group-by';
import { useGroupCacheActions } from '../group-cache-actions';
import { useHierarchyStore } from '../hierarchy';
import { useHierarchyConfig } from '../hierarchy/hierarchy-config';
import { useSupportedFieldIds } from '../use-supported-field-ids';

// Loads individual items, dealing with the cache updates
export const useListViewUpdaterService = (): UpdaterReturn => {
	const [{ selectedIssues, showDeleteModal }, { toggleShowDeleteModal, unselectIssues }] =
		useBulkSelectionStore();
	const [{ hierarchyLevel }] = useHierarchyLevel();
	const { jqlContext, quickFiltersJql, sorting, isHidingDoneItems } = useFilteredViewContextInput({
		view: 'list',
	});
	const fieldIds = useSupportedFieldIds();
	const { hierarchyParent } = useHierarchyConfig();
	const { addToCache, removeFromCache, updateItemsInCache } = useCacheActions();
	const [{ issueParentMap, issuesMap, childrenItems: childrenQueryData, hierarchyData }] =
		useHierarchyStore();
	let projectKey: string | undefined;
	if (ff('list-uses-new-jql-parent-project-directive_v5gfs')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const projectData = useProject();

		projectKey = projectData.key;
	}
	const groups: ListViewIssue[] = hierarchyData.data;
	const [groupByFieldId] = useGroupBy();
	const isGroupingByStatus = groupByFieldId === STATUS_TYPE;

	const cloudId = useCloudId();

	const topLevelVariables = {
		...getVariables({
			jqlContext,
			fieldIds,
			quickFiltersJql,
			sorting,
			hierarchyLevel,
			hierarchyParent,
			isHidingDoneItems,
			projectIdOrKey: projectKey,
		}),
		cloudId,
	};

	const { addToCacheCallback, removeFromCacheCallback } =
		createViewUpdaterCallbacksWithCacheActions<JWMListViewItemDetailsNode, JWMListViewItemDetails>({
			query: LIST_VIEW_QUERY,
			addToCache,
			updateItemsInCache,
			removeFromCache,
			isHierarchyEnabled: () => typeof hierarchyParent !== 'undefined',
			isInfiniteHierarchyEnabled: () => typeof hierarchyParent !== 'undefined',
			createVariablesForItemWithoutParent: () => topLevelVariables,
			createVariablesForGroupItem: (groupIssueId) => ({
				cloudId,
				...getVariables({
					jqlContext,
					fieldIds,
					quickFiltersJql,
					sorting,
					groupQuery: getGroupQuery({ groups, groupIssueId, groupByFieldId }),
					hierarchyParent,
					isHidingDoneItems,
				}),
			}),
			createVariablesForItemWithParent: (parentIssueId) => {
				if (!hierarchyParent) {
					// this scenario should be impossible because there is isHierarchyEnabled parameter above
					return topLevelVariables;
				}

				return {
					...getVariables({
						jqlContext,
						fieldIds,
						quickFiltersJql,
						sorting,
						isHidingDoneItems,
						hierarchyParent: undefined,
						projectIdOrKey: projectKey,
						parentIssue: {
							hierarchyType: hierarchyParent,
							issueId: parentIssueId,
						},
					}),
					cloudId,
				};
			},
			// We only want to add or remove item if the existing query for children-of-this-parent exists
			// this is to prevent apollo error where the cache key is missing
			shouldChildItemBeAddedToOrRemovedFromCache: (parentIssueId) =>
				Boolean(childrenQueryData && childrenQueryData[toIssueId(parentIssueId)] !== undefined),
			getParentOfIssueIfExist: (issueId) => {
				const maybeIssue = issuesMap.get(issueId);
				if (!maybeIssue) {
					return undefined;
				}
				return maybeIssue.parentIssueId ?? getParentIssueId(maybeIssue);
			},
			getPrevParentOfIssueIfExists: (issueId) => issueParentMap.get(String(issueId)),
			onUpdateParentWithNewChild: addChildToParentListView,
			// note that prevParent will be undefined if the prevParent is not expanded or does not exist
			updateIssueParentMap: (issueId, newParentId) => {
				if (newParentId) {
					issueParentMap.set(String(issueId), String(newParentId));
				} else {
					issueParentMap.delete(String(issueId));
				}
			},
			removeChildFromParent: removeChildFromParentListView,
			isGroupingByStatus,
			isGrouping: !!groupByFieldId,
		});

	const isGrouping = groupByFieldId != null;
	const { addToGroupCache, removeFromGroupCache } = useGroupCacheActions({ fieldIds });

	// When we detect an item has been updated that is currently filtered out of view, we still want to update group totals
	// so we do another request with no filters and update group cache's total count only
	const [getUpdatedIssue] = useLazyQuery<JWMListViewItemDetails, JWMListViewItemDetailsVariables>(
		LIST_VIEW_ITEM_DETAILS_QUERY,
		{
			fetchPolicy: 'network-only',
			onError: (apolloError) => {
				fireErrorAnalytics({
					meta: {
						id: 'listViewUpdaterQuery',
						packageName: 'jiraBusinessList',
						teamName: 'keystone',
					},
					attributes: {
						message: 'Failed to fetch updated issue by id in list view updater',
					},
					error: apolloError,
					sendToPrivacyUnsafeSplunk: true,
				});
			},
			onCompleted: (result) => {
				const items = getAGGCompliantData<JWMListViewItemDetails, JWMListViewItemDetailsNode>(
					result,
					'list',
				);
				const validNodes = items?.edges?.map((edge) => edge?.node).filter(Boolean);
				if (validNodes && validNodes.length > 0) {
					addToGroupCache(validNodes, { updateCountOnly: true });
				}
			},
		},
	);
	const handleFilteredUpdate = (issueIds: number[]) => {
		getUpdatedIssue({
			variables: {
				...getVariables({
					issueIds,
					fieldIds,
				}),
				cloudId,
			},
		});
	};
	const getGroupChildItems = (items: JWMListViewItemDetailsNode[]) => {
		const groupChildItems: JWMListViewItemDetailsNode[] = [];
		const nestedItems: JWMListViewItemDetailsNode[] = [];
		items.forEach((item) => {
			const parentIssueId = getParentIssueId(item);
			// we only want to move items between groups via realtime update if the item is a direct child of a group item
			// that is when it doesn't have a parent field, or its parent is a group item,
			if (
				parentIssueId != null &&
				!isGroupItem(parentIssueId) &&
				childrenQueryData &&
				childrenQueryData[parentIssueId]
			) {
				nestedItems.push(item);
			} else {
				groupChildItems.push(item);
			}
		});
		return { groupChildItems, nestedItems };
	};

	return useViewUpdater<
		JWMListViewItemDetailsNode,
		JWMListViewItemDetailsEdges,
		JWMListViewItemDetails
	>({
		input: {
			projectKey,
			fieldIds,
			quickFiltersJql,
			isHidingDoneItems,
		},
		query: LIST_VIEW_ITEM_DETAILS_QUERY,
		actions: [
			{
				addToCache: (items) => {
					if (isGrouping) {
						const { groupChildItems, nestedItems } = getGroupChildItems(items);
						if (groupChildItems.length) {
							return addToGroupCache(groupChildItems);
						}
						if (nestedItems.length) {
							return addToCacheCallback(items);
						}
					}
					return addToCacheCallback(items);
				},
				removeFromCache: (items) => {
					if (selectedIssues?.size) {
						let issuesToUnselect: string[] = [];
						items.forEach(({ issueId }) => {
							if (selectedIssues.has(String(issueId))) {
								issuesToUnselect = [...issuesToUnselect, String(issueId)];
							}
						});
						if (issuesToUnselect.length) {
							unselectIssues({ issues: issuesToUnselect });
							if (issuesToUnselect.length === selectedIssues.size && showDeleteModal) {
								toggleShowDeleteModal({ showDeleteModal: false });
							}
						}
					}

					if (isGrouping) {
						const { groupChildItems, nestedItems } = getGroupChildItems(items);
						if (groupChildItems.length) {
							return removeFromGroupCache(groupChildItems);
						}
						if (nestedItems.length) {
							return removeFromCacheCallback(items);
						}
					}
					return removeFromCacheCallback(items);
				},
				handleFilteredUpdate: (issueIds: number[]) =>
					isGrouping ? handleFilteredUpdate(issueIds) : undefined,
			},
		],

		onError: (apolloError) => {
			fireErrorAnalytics({
				meta: {
					id: 'listViewUpdaterQuery',
					packageName: 'jiraBusinessList',
					teamName: 'keystone',
				},
				attributes: {
					message: 'Failed to fetch issues in list view updater',
				},
				error: apolloError,
				sendToPrivacyUnsafeSplunk: true,
			});
		},
		onAppendItemsError: (apolloError) => {
			fireErrorAnalytics({
				meta: {
					id: 'listViewAppendItemsQuery',
					packageName: 'jiraBusinessList',
					teamName: 'keystone',
				},
				attributes: {
					message: 'Failed to append issues by id in list view updater',
				},
				error: apolloError,
				sendToPrivacyUnsafeSplunk: true,
			});
		},
	});
};
